import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { adminAuthHeaders } from './adminCheckoutUtils';
import { exportBespokeRequestsCsv } from './adminExportUtils';
import {
  AdminPanelAlert,
  AdminPanelEmpty,
  AdminPanelHero,
  AdminPanelListCard,
  AdminPanelToolbar,
} from './AdminPanelUi';
import AdminCustomerPhotoBlock, { printBespokePhotos } from '../jewelry/AdminCustomerPhotoBlock';
import { parseBespokePhotos } from '../jewelry/customerPhotoUpload';
import {
  BESPOKE_STATUSES,
  bespokeJewelryTypesLabel,
  bespokeOccasionLabel,
  bespokeStatusLabel,
} from '../jewelry/bespokeFormOptions';
import { useSiteSettings } from '../SiteSettingsContext';

function formatWhen(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function customerInitial(name) {
  const c = String(name || 'C').trim().charAt(0);
  return c ? c.toUpperCase() : 'C';
}

export default function AdminBespokeRequestsPanel({ sessionToken, setStatus }) {
  const { settings } = useSiteSettings();
  const storeCopy = settings?.store_copy;
  const [requests, setRequests] = useState([]);
  const [counts, setCounts] = useState({});
  const [tableReady, setTableReady] = useState(true);
  const [migrationHint, setMigrationHint] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [statusFilter, setStatusFilter] = useState('new');
  const [searchDraft, setSearchDraft] = useState('');
  const [search, setSearch] = useState('');
  const [loadingList, setLoadingList] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [error, setError] = useState('');
  const [statusDraft, setStatusDraft] = useState('new');
  const [adminNotesDraft, setAdminNotesDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);

  const loadList = useCallback(async () => {
    setLoadingList(true);
    setError('');
    try {
      const qs = new URLSearchParams();
      if (statusFilter) qs.set('status', statusFilter);
      if (search.trim()) qs.set('q', search.trim());
      const r = await fetch(`/api/admin_bespoke_requests.php?${qs}`, {
        headers: adminAuthHeaders(sessionToken),
      });
      const d = await r.json();
      if (!r.ok || !d.success) {
        setError(d.error || 'Could not load custom requests.');
        setRequests([]);
        return;
      }
      setTableReady(d.table_ready !== false);
      setMigrationHint(d.message || '');
      setRequests(Array.isArray(d.requests) ? d.requests : []);
      setCounts(d.counts && typeof d.counts === 'object' ? d.counts : {});
    } catch {
      setError('Could not load custom requests.');
      setRequests([]);
    } finally {
      setLoadingList(false);
    }
  }, [sessionToken, statusFilter, search]);

  const loadDetail = useCallback(
    async (id) => {
      if (!id) {
        setDetail(null);
        return;
      }
      setLoadingDetail(true);
      setError('');
      try {
        const r = await fetch(`/api/admin_bespoke_requests.php?id=${id}`, {
          headers: adminAuthHeaders(sessionToken),
        });
        const d = await r.json();
        if (!r.ok || !d.success || !d.request) {
          setError(d.error || 'Could not load request.');
          setDetail(null);
          return;
        }
        setDetail(d.request);
        setStatusDraft(d.request.status || 'new');
        setAdminNotesDraft(d.request.admin_notes || '');
      } catch {
        setError('Could not load request.');
        setDetail(null);
      } finally {
        setLoadingDetail(false);
      }
    },
    [sessionToken]
  );

  useEffect(() => {
    loadList();
  }, [loadList]);

  useEffect(() => {
    if (selectedId) loadDetail(selectedId);
    else setDetail(null);
  }, [selectedId, loadDetail]);

  const newCount = Number(counts.new || 0);

  const selectRequest = (id) => {
    setSelectedId(id);
  };

  const saveDetail = async () => {
    if (!detail?.id) return;
    setSaving(true);
    setError('');
    try {
      const r = await fetch('/api/admin_bespoke_requests.php', {
        method: 'POST',
        headers: adminAuthHeaders(sessionToken),
        body: JSON.stringify({
          action: 'update',
          id: detail.id,
          status: statusDraft,
          admin_notes: adminNotesDraft,
        }),
      });
      const d = await r.json();
      if (!r.ok || !d.success) {
        setError(d.error || 'Could not save changes.');
        return;
      }
      setStatus('Custom request updated.');
      if (d.request) {
        setDetail(d.request);
        setStatusDraft(d.request.status || 'new');
        setAdminNotesDraft(d.request.admin_notes || '');
      }
      await loadList();
    } catch {
      setError('Could not save changes.');
    } finally {
      setSaving(false);
    }
  };

  const importLog = async () => {
    setImporting(true);
    setError('');
    try {
      const r = await fetch('/api/admin_bespoke_requests.php', {
        method: 'POST',
        headers: adminAuthHeaders(sessionToken),
        body: JSON.stringify({ action: 'import_log' }),
      });
      const d = await r.json();
      if (!r.ok || !d.success) {
        setError(d.error || 'Import failed.');
        return;
      }
      setStatus(
        `Imported ${d.imported || 0} request(s) from log file${d.skipped ? ` (${d.skipped} skipped as duplicates).` : '.'}`
      );
      await loadList();
    } catch {
      setError('Import failed.');
    } finally {
      setImporting(false);
    }
  };

  const statusTabs = useMemo(
    () => [
      { value: '', label: 'All', count: Object.values(counts).reduce((a, b) => a + Number(b || 0), 0) },
      ...BESPOKE_STATUSES.map((s) => ({
        value: s.value,
        label: s.label,
        count: Number(counts[s.value] || 0),
      })),
    ],
    [counts]
  );

  return (
    <div className="admin-bespoke admin-panel-workspace-root">
      <AdminPanelHero
        title="Custom jewelry requests"
        lead="Design enquiries from the /custom-jewelry page — outfit photos, occasion, and customer contact details."
        badge={newCount > 0 ? `${newCount} new` : null}
        badgeWarn={newCount > 0}
        actions={
          <AdminPanelToolbar>
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              disabled={!requests.length}
              onClick={() => exportBespokeRequestsCsv(requests, storeCopy)}
            >
              Export CSV
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              disabled={importing || !tableReady}
              onClick={importLog}
              title="Import older submissions saved in bespoke_orders.log before the database inbox existed"
            >
              {importing ? 'Importing…' : 'Import log file'}
            </button>
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={loadList}>
              Refresh
            </button>
          </AdminPanelToolbar>
        }
      />

      {!tableReady ? (
        <AdminPanelAlert variant="warning">
          {migrationHint || 'Run database/migrate_bespoke_requests.sql on your MySQL database, then refresh.'}
        </AdminPanelAlert>
      ) : null}

      {error ? <AdminPanelAlert variant="danger">{error}</AdminPanelAlert> : null}

      <div className="row g-3 align-items-start">
        <div className="col-lg-5">
          <AdminPanelListCard
            kicker="Inbox"
            title="Requests"
            count={requests.length || null}
          >
            <div className="admin-bespoke-filters mb-2">
              <div className="btn-group btn-group-sm flex-wrap" role="group" aria-label="Filter by status">
                {statusTabs.map((tab) => (
                  <button
                    key={tab.value || 'all'}
                    type="button"
                    className={`btn btn-outline-secondary${statusFilter === tab.value ? ' active' : ''}`}
                    onClick={() => setStatusFilter(tab.value)}
                  >
                    {tab.label}
                    {tab.count ? ` (${tab.count})` : ''}
                  </button>
                ))}
              </div>
              <div className="input-group input-group-sm mt-2">
                <input
                  type="search"
                  className="form-control"
                  placeholder="Search name, email, phone…"
                  value={searchDraft}
                  onChange={(e) => setSearchDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') setSearch(searchDraft);
                  }}
                />
                <button type="button" className="btn btn-primary" onClick={() => setSearch(searchDraft)}>
                  Search
                </button>
              </div>
            </div>

            {loadingList ? (
              <p className="small text-muted mb-0">Loading requests…</p>
            ) : requests.length === 0 ? (
              <AdminPanelEmpty title="No requests yet" hint="Submissions from /custom-jewelry will appear here." />
            ) : (
              <ul className="admin-bespoke-list list-unstyled mb-0">
                {requests.map((req) => {
                  const active = selectedId === req.id;
                  return (
                    <li key={req.id} className={`admin-bespoke-row${active ? ' admin-bespoke-row--active' : ''}`}>
                      <button type="button" className="admin-bespoke-row__open" onClick={() => selectRequest(req.id)}>
                        <span className="admin-bespoke-row__avatar" aria-hidden="true">
                          {customerInitial(req.name)}
                        </span>
                        <span className="admin-bespoke-row__content">
                          <span className="admin-bespoke-row__line1">
                            <span className="admin-bespoke-row__id">#{req.request_number}</span>
                            <span className={`admin-bespoke-row__status admin-bespoke-row__status--${req.status}`}>
                              {bespokeStatusLabel(req.status)}
                            </span>
                          </span>
                          <span className="admin-bespoke-row__line2">{req.name}</span>
                          <span className="admin-bespoke-row__line3">
                            {bespokeOccasionLabel(req.occasion, storeCopy)} · {bespokeJewelryTypesLabel(req.jewelry_type, storeCopy)}
                          </span>
                          <span className="admin-bespoke-row__line4">{formatWhen(req.created_at)}</span>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </AdminPanelListCard>
        </div>

        <div className="col-lg-7">
          {!selectedId ? (
            <div className="admin-bespoke-detail-empty">
              <p className="mb-0 text-muted">Select a request to view photos, contact details, and update status.</p>
            </div>
          ) : loadingDetail && !detail ? (
            <p className="small text-muted">Loading request…</p>
          ) : detail ? (
            <div className="admin-bespoke-detail card border-0">
              <div className="admin-bespoke-detail__hero">
                <div>
                  <span className="admin-bespoke-detail__eyebrow">Custom jewelry request</span>
                  <h2 className="admin-bespoke-detail__number">#{detail.request_number}</h2>
                  <p className="admin-bespoke-detail__meta small text-muted mb-0">
                    Received {formatWhen(detail.created_at)}
                    {detail.event_date ? ` · Event ${detail.event_date}` : ''}
                  </p>
                </div>
                <div className="admin-bespoke-detail__actions">
                  {(() => {
                    const hasPhotos =
                      parseBespokePhotos(detail.dress_photo).length > 0 ||
                      parseBespokePhotos(detail.reference_photo).length > 0;
                    return hasPhotos ? (
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm"
                      onClick={() => printBespokePhotos(detail)}
                    >
                      Print photos
                    </button>
                    ) : null;
                  })()}
                </div>
              </div>

              <div className="admin-bespoke-detail__body">
                <section className="admin-bespoke-section">
                  <h3 className="admin-bespoke-section__title">Customer</h3>
                  <dl className="admin-bespoke-dl">
                    <div>
                      <dt>Name</dt>
                      <dd>{detail.name}</dd>
                    </div>
                    <div>
                      <dt>Email</dt>
                      <dd>
                        <a href={`mailto:${detail.email}`}>{detail.email}</a>
                      </dd>
                    </div>
                    <div>
                      <dt>Phone</dt>
                      <dd>
                        <a href={`tel:${detail.phone}`}>{detail.phone}</a>
                      </dd>
                    </div>
                  </dl>
                </section>

                <section className="admin-bespoke-section">
                  <h3 className="admin-bespoke-section__title">Brief</h3>
                  <dl className="admin-bespoke-dl">
                    <div>
                      <dt>Occasion</dt>
                      <dd>{bespokeOccasionLabel(detail.occasion, storeCopy)}</dd>
                    </div>
                    <div>
                      <dt>Jewelry type</dt>
                      <dd>{bespokeJewelryTypesLabel(detail.jewelry_type, storeCopy)}</dd>
                    </div>
                    {detail.event_date ? (
                      <div>
                        <dt>Event date</dt>
                        <dd>{detail.event_date}</dd>
                      </div>
                    ) : null}
                    {detail.notes ? (
                      <div>
                        <dt>Customer notes</dt>
                        <dd className="admin-bespoke-notes">{detail.notes}</dd>
                      </div>
                    ) : null}
                  </dl>
                </section>

                <section className="admin-bespoke-section">
                  <h3 className="admin-bespoke-section__title">Photos</h3>
                  <div className="admin-bespoke-photos">
                    {(() => {
                      const dressPhotos =
                        detail.dress_photos?.length > 0
                          ? detail.dress_photos
                          : parseBespokePhotos(detail.dress_photo);
                      const referencePhotos =
                        detail.reference_photos?.length > 0
                          ? detail.reference_photos
                          : parseBespokePhotos(detail.reference_photo);
                      return (
                        <>
                          <div>
                            <p className="small fw-semibold mb-2">
                              Outfit / dress colour{dressPhotos.length > 1 ? ` (${dressPhotos.length})` : ''}
                            </p>
                            {dressPhotos.length > 0 ? (
                              <div className="admin-bespoke-photos__group">
                                {dressPhotos.map((url, index) => (
                                  <AdminCustomerPhotoBlock
                                    key={`dress-${url}-${index}`}
                                    url={url}
                                    label={`Outfit photo ${index + 1}`}
                                    filePrefix={detail.request_number}
                                  />
                                ))}
                              </div>
                            ) : (
                              <p className="small text-muted mb-0">No outfit photos uploaded.</p>
                            )}
                          </div>
                          <div>
                            <p className="small fw-semibold mb-2">
                              Reference / inspiration
                              {referencePhotos.length > 1 ? ` (${referencePhotos.length})` : ''}
                            </p>
                            {referencePhotos.length > 0 ? (
                              <div className="admin-bespoke-photos__group">
                                {referencePhotos.map((url, index) => (
                                  <AdminCustomerPhotoBlock
                                    key={`ref-${url}-${index}`}
                                    url={url}
                                    label={`Reference photo ${index + 1}`}
                                    filePrefix={detail.request_number}
                                  />
                                ))}
                              </div>
                            ) : (
                              <p className="small text-muted mb-0">No reference photos uploaded.</p>
                            )}
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </section>

                <section className="admin-bespoke-section">
                  <h3 className="admin-bespoke-section__title">Workflow</h3>
                  <div className="row g-2">
                    <div className="col-md-6">
                      <label className="form-label small" htmlFor="bespoke-status">
                        Status
                      </label>
                      <select
                        id="bespoke-status"
                        className="form-select form-select-sm"
                        value={statusDraft}
                        onChange={(e) => setStatusDraft(e.target.value)}
                      >
                        {BESPOKE_STATUSES.map((s) => (
                          <option key={s.value} value={s.value}>
                            {s.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="mt-2">
                    <label className="form-label small" htmlFor="bespoke-admin-notes">
                      Internal notes (not shown to customer)
                    </label>
                    <textarea
                      id="bespoke-admin-notes"
                      className="form-control form-control-sm"
                      rows={4}
                      value={adminNotesDraft}
                      onChange={(e) => setAdminNotesDraft(e.target.value)}
                      placeholder="Quote sent, fabric swatch matched, follow-up date…"
                    />
                  </div>
                  <div className="mt-3 d-flex flex-wrap gap-2">
                    <button type="button" className="btn btn-primary btn-sm" disabled={saving} onClick={saveDetail}>
                      {saving ? 'Saving…' : 'Save'}
                    </button>
                    {detail.mail_sent === false ? (
                      <span className="small text-warning align-self-center">Confirmation email may not have sent.</span>
                    ) : null}
                  </div>
                </section>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
